import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { post } from "../../lib/request"

const TOKEN_KEY = "auth_token"
const USER_KEY = "auth_user"
const REMEMBERED_EMAIL_KEY = "auth_remembered_email"

function clearAuthStorageOnly() {
  try {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(USER_KEY)
    localStorage.removeItem("user_preferences")
  } catch {}
}

export const logoutUser = createAsyncThunk("auth/logoutUser", async (_, { rejectWithValue }) => {
  try {
    await post("authn/logout", {})
    return true
  } catch (error) {
    console.error("Logout API failed:", error)
    return rejectWithValue(error?.message || "Logout failed")
  }
})

function readStoredAuth() {
  try {
    let token = localStorage.getItem(TOKEN_KEY)
    let userRaw = localStorage.getItem(USER_KEY)
    if (!token || !userRaw) {
      token = sessionStorage.getItem(TOKEN_KEY)
      userRaw = sessionStorage.getItem(USER_KEY)
    }
    if (!token || token.length <= 10) return { token: null, user: null }
    let user = null
    try {
      user = userRaw ? JSON.parse(userRaw) : null
    } catch {
      user = null
    }
    return { token, user }
  } catch {
    return { token: null, user: null }
  }
}

export const validateTokenAndHydrate = createAsyncThunk(
  "auth/validateTokenAndHydrate",
  async (_, { rejectWithValue }) => {
    try {
      const stored = readStoredAuth()
      if (!stored.token) {
        clearAuthStorageOnly()
        return null
      }
      return stored
    } catch (error) {
      console.error("Token validation failed:", error)
      try {
        clearAuthStorageOnly()
      } catch {}
      return null
    }
  },
)

export const login = createAsyncThunk(
  "auth/login",
  async ({ username, password, rememberMe = false }, { rejectWithValue }) => {
    try {
      const response = await post("authn/login", { username, password }, { simple: true, skipAuth: true })

      const token = response?.data?.accessToken
      const user = { username }

      if (token) {
        try {
          if (rememberMe) {
            localStorage.setItem(TOKEN_KEY, token)
            localStorage.setItem(USER_KEY, JSON.stringify(user))
            localStorage.setItem(REMEMBERED_EMAIL_KEY, username || "")
          } else {
            sessionStorage.setItem(TOKEN_KEY, token)
            sessionStorage.setItem(USER_KEY, JSON.stringify(user))
            localStorage.removeItem(REMEMBERED_EMAIL_KEY)
          }
        } catch (error) {}
      }

      return { token, user }
    } catch (err) {
      const errorMessage = err?.message
      return rejectWithValue(errorMessage)
    }
  },
)

export const signup = createAsyncThunk(
  "auth/signup",
  async ({ username, email, password }, { rejectWithValue, getState }) => {
    try {
      // Get the auth token from state
      const state = getState()
      const token = state.auth?.token

      const res = await fetch("https://dev-asdp.bcv.social/api/v1/authn/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ username, email, password }),
      })

      if (!res.ok) {
        // try to extract message from JSON or plain text
        let message = "Signup failed"
        try {
          const data = await res.json()
          message = data?.message || data?.error || message
        } catch {
          const text = await res.text().catch(() => "")
          if (text) message = text
        }
        return rejectWithValue(message)
      }

      // return response payload if needed by caller
      const data = await res.json().catch(() => ({}))
      return data
    } catch (err) {
      console.error("Signup exception:", err)
      return rejectWithValue(err?.message || "Signup failed")
    }
  },
)

const storedAuth = readStoredAuth()

const initialState = {
  user: storedAuth.user,
  token: storedAuth.token,
  status: "idle",
  error: null,
  signUpStatus: "idle",
  signUpError: null,
  isInitialized: true,
}

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (state, action) => {
      state.user = action.payload?.user || null
      state.token = action.payload?.token || null
      state.isInitialized = true

      if (action.payload?.token) {
        try {
          localStorage.setItem("auth_token", action.payload.token)
          localStorage.setItem("auth_user", JSON.stringify(action.payload.user))
        } catch (error) {
          console.error("Failed to store credentials in localStorage:", error)
        }
      }
    },
    setInitialized: (state) => {
      state.isInitialized = true
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.status = "idle"
      state.error = null
      state.isInitialized = true
      try {
        clearAuthStorageOnly()
      } catch {}
    },
    clearError: (state) => {
      state.error = null
    },
    clearSignUpState: (state) => {
      state.signUpStatus = "idle"
      state.signUpError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.user = action.payload.user
        state.token = action.payload.token
        state.isInitialized = true
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Login failed"
      })
      .addCase(signup.pending, (state) => {
        state.signUpStatus = "loading"
        state.signUpError = null
      })
      .addCase(signup.fulfilled, (state) => {
        state.signUpStatus = "succeeded"
      })
      .addCase(signup.rejected, (state, action) => {
        state.signUpStatus = "failed"
        state.signUpError = action.payload || "Signup failed"
      })
      .addCase(validateTokenAndHydrate.fulfilled, (state, action) => {
        if (action.payload) {
          state.user = action.payload.user
          state.token = action.payload.token
          if (state.status !== "loading") {
            state.status = "succeeded"
          }
        } else if (state.status === "loading") {
          state.status = "idle"
        }
        state.isInitialized = true
      })
      .addCase(validateTokenAndHydrate.rejected, (state) => {
        state.user = null
        state.token = null
        state.status = "idle"
        state.isInitialized = true
      })
      .addCase(logoutUser.pending, (state) => {
        state.status = "loading"
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null
        state.token = null
        state.status = "idle"
        state.error = null
        state.isInitialized = true
        try {
          clearAuthStorageOnly()
        } catch {}
      })
      .addCase(logoutUser.rejected, (state) => {
        state.user = null
        state.token = null
        state.status = "idle"
        state.error = null
        state.isInitialized = true
        try {
          clearAuthStorageOnly()
        } catch {}
      })
  },
})

export const { setCredentials, logout, clearError, clearSignUpState, setInitialized } = authSlice.actions
export { REMEMBERED_EMAIL_KEY }

export function hydrateFromStorage() {
  return (dispatch) => {
    dispatch(validateTokenAndHydrate())
  }
}

export default authSlice.reducer
